import * as React from 'react';
import { cn } from '../lib/utils';
import { Popover } from './popover';
import { Tooltip } from './tooltip';

export const LIST_ICON_BUTTON_CLASS =
  'rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground';

export interface ListFilterOption {
  value: string;
  label: string;
  testId?: string;
  trailing?: React.ReactNode;
}

export interface ListFilter {
  key: string;
  label: string;
  value: string;
  options: ListFilterOption[];
  onChange: (value: string) => void;
  footer?: React.ReactNode;
  testId?: string;
}

function IconFilters() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="4" x2="20" y1="8" y2="8" />
      <line x1="4" x2="20" y1="16" y2="16" />
      <line x1="9" x2="9" y1="5" y2="11" />
      <line x1="15" x2="15" y1="13" y2="19" />
    </svg>
  );
}

function IconPlus() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </svg>
  );
}

function IconBack() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function rowButtonClass(active?: boolean) {
  return cn(
    'flex w-full items-center justify-between gap-3 rounded px-2 py-1.5 text-left text-caption',
    active ? 'bg-accent' : 'hover:bg-accent',
  );
}

export function ListFilterMenu({ filters, testId }: { filters: ListFilter[]; testId: string }) {
  const [open, setOpen] = React.useState(false);
  const [panel, setPanel] = React.useState<string | null>(null);
  const active = filters.some((f) => f.options.length > 0 && f.value !== f.options[0].value);
  const current = filters.find((f) => f.key === panel);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setPanel(null);
      }}
      align="start"
      portal
      aria-label="Filters"
      triggerClassName={cn(LIST_ICON_BUTTON_CLASS, 'relative', active && 'text-foreground')}
      contentClassName="w-60 p-1"
      trigger={
        <span data-testid={testId} data-active={active} className="inline-flex">
          <Tooltip label="Filters">
            <span className="inline-flex" aria-label="Filters">
              <IconFilters />
            </span>
          </Tooltip>
          {active && <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />}
        </span>
      }
    >
      {current ? (
        <div className="flex flex-col">
          <button type="button" className={cn(rowButtonClass(), 'justify-start text-muted-foreground')} onClick={() => setPanel(null)}>
            <IconBack />
            {current.label}
          </button>
          {current.options.map((option) => (
            <div key={option.value} className="group flex items-center gap-1">
              <button
                type="button"
                className={cn(rowButtonClass(option.value === current.value), 'min-w-0 flex-1')}
                data-testid={option.testId}
                data-value={option.value}
                aria-pressed={option.value === current.value}
                onClick={() => {
                  current.onChange(option.value);
                  setPanel(null);
                  setOpen(false);
                }}
              >
                <span className="truncate">{option.label}</span>
                {option.value === current.value && <IconCheck />}
              </button>
              {option.trailing}
            </div>
          ))}
          {current.footer && <div className="mt-1 border-t pt-1">{current.footer}</div>}
        </div>
      ) : (
        <div className="flex flex-col">
          {filters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={rowButtonClass()}
              data-testid={filter.testId}
              data-value={filter.value}
              onClick={() => setPanel(filter.key)}
            >
              <span>{filter.label}</span>
              <span className="truncate text-muted-foreground">
                {filter.options.find((o) => o.value === filter.value)?.label ?? filter.value}
              </span>
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}

export const ListAddButton = React.forwardRef<
  HTMLButtonElement,
  { label: string; onClick?: () => void; testId?: string; disabled?: boolean }
>(function ListAddButton({ label, onClick, testId, disabled }, ref) {
  return (
    <Tooltip label={label}>
      <button
        ref={ref}
        type="button"
        className={cn(LIST_ICON_BUTTON_CLASS, 'disabled:pointer-events-none disabled:opacity-50')}
        data-testid={testId}
        disabled={disabled}
        onClick={onClick}
      >
        <IconPlus />
      </button>
    </Tooltip>
  );
});

export interface ListAddMenuItem {
  label: string;
  onClick: () => void;
  testId?: string;
  disabled?: boolean;
}

export function ListAddMenu({ label, testId, items }: { label: string; testId?: string; items: ListAddMenuItem[] }) {
  const [open, setOpen] = React.useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      portal
      aria-label={label}
      triggerClassName={LIST_ICON_BUTTON_CLASS}
      contentClassName="w-56 p-1"
      trigger={
        <span data-testid={testId} className="inline-flex">
          <Tooltip label={label}>
            <span className="inline-flex" aria-label={label}>
              <IconPlus />
            </span>
          </Tooltip>
        </span>
      }
    >
      <div role="menu" className="flex flex-col">
        {items.map((item) => (
          <button
            key={item.label}
            type="button"
            role="menuitem"
            className={cn(rowButtonClass(), 'disabled:cursor-not-allowed disabled:opacity-50')}
            data-testid={item.testId}
            disabled={item.disabled}
            onClick={() => {
              setOpen(false);
              item.onClick();
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
    </Popover>
  );
}
